import { ensureRouteData, prefetchRouteData } from '@/app/query-client'
import {
  largeTradeLeadersQueryOptions,
  largeTradesQueryOptions,
} from '@/features/brokers/api/queries'
import type { RouterContext } from '@/routes/layouts/root'
import {
  largeTradesSearchSchema,
  toLargeTradeLeadersQuery,
  toLargeTradesQuery,
  toLargeTradesTodayQuery,
} from '@/screens/brokers/search'

/**
 * The `/brokers/large-trades` loader.
 *
 * MAIN: the trades read, keyed on the filters (`page`/`pageSize`/`stock`/`broker`; the floor is
 * the mock's fixed Rs 10 L) — the table and its pager.
 *
 * SECONDARY, prefetched and not awaited, and only on a REAL navigation
 * (`!preload`) — docs/route-loading.md, "Secondary reads": the leaderboard,
 * which `BrokerLeadersCard` draws its own skeleton and error for, and today's unfiltered read
 * (the summary band, the menus' options), which does not follow the table's filters. This is one
 * of the family's densest, most-linked tables (every trade row links a stock,
 * every leader a broker), so a hover pass over it must not also fire a burst
 * of leaderboard requests for every row the cursor crosses; main data still
 * loads on hover.
 *
 * Because main data here is keyed on search params, the screen does NOT read
 * it with a suspense query — `useLargeTrades` stays plain `useQuery` +
 * `keepPreviousData` + `refetchInterval` — see docs/route-loading.md, "Search
 * params". This loader only makes sure the ENTRY url's query is in the cache
 * before the page mounts.
 */
export async function loadLargeTrades({
  context: { queryClient },
  location,
  preload,
}: {
  context: RouterContext
  location: { search: unknown }
  preload: boolean
}): Promise<void> {
  const search = largeTradesSearchSchema.parse(location.search)
  if (!preload) {
    prefetchRouteData(queryClient, largeTradeLeadersQueryOptions(toLargeTradeLeadersQuery()))
    // The summary band and the menus' options: today's whole read, whatever the table filters to.
    prefetchRouteData(queryClient, largeTradesQueryOptions(toLargeTradesTodayQuery()))
  }
  // Through `toLargeTradesQuery`, the same picker the screen's own
  // `useLargeTrades` call goes through — see search.ts's own comment on why
  // `search` itself is not safe to hand to `largeTradesQueryOptions`.
  await ensureRouteData(queryClient, largeTradesQueryOptions(toLargeTradesQuery(search)))
}
